import { useEffect, useMemo } from 'react'

import { Divider, Form, FormInstance } from 'antd'

import { TemplateData } from '../types/blueprint'

import { DisplayDescription } from './DisplayBlock.style'
import Section from './Section'
import { TemplateAppendix } from './TemplateAppendix'
import mapCaptureToDisplay from './utils/mapCaptureToDisplay'
import { TEMPLATE_RESPONSE_DATA_FIELD } from './utils/mapValuesToTemplate'

import {
  ConfigOptions,
  RenderOptions,
  TemplateRendererMode,
  TemplateRendererProvider,
  useTemplateRenderer,
} from '@/context/templateRendererContext'
import { useUser } from '@/context/userContext'

import { Resource } from 'types/api.graphql'

export type TemplateResponse = {
  id: string
  responseData?: TemplateData
  template?: {
    templateData?: TemplateData
    templateName?: string
  }
}

type BaseProps = {
  projectId: string // mostly used for attachments uploaded within the template
  resource: Resource // mostly used for attachments uploaded within the template
  renderOptions?: RenderOptions
  extraInfo?: Record<string, unknown>
  overrideBuilderId?: string
  config?: ConfigOptions
  children?: React.ReactNode
}

type NewProps = BaseProps & {
  form: FormInstance
  templateData: TemplateData
}

/**
 * Used when creating a new template response
 */
export const TemplateRendererNew: React.FC<NewProps> = ({
  form,
  templateData,
  projectId,
  resource,
  renderOptions,
  extraInfo,
  config,
  children,
}) => {
  return (
    <TemplateRenderer
      mode="capture"
      form={form}
      isCapture
      originalTemplate={templateData}
      template={templateData}
      templateResponseId={null}
      renderOptions={renderOptions}
      projectId={projectId}
      extraInfo={extraInfo}
      resource={resource}
      config={config}
    >
      {children}
    </TemplateRenderer>
  )
}

type EditProps = BaseProps & {
  form: FormInstance
  templateResponse: TemplateResponse
}

/**
 * Used when editing an existing template response
 */
export const TemplateRendererEdit: React.FC<EditProps> = ({
  form,
  templateResponse,
  renderOptions,
  overrideBuilderId,
  projectId,
  resource,
  extraInfo,
  config,
  children,
}) => {
  return (
    <TemplateRenderer
      mode="capture"
      form={form}
      isCapture
      template={templateResponse?.responseData}
      originalTemplate={templateResponse?.template?.templateData}
      templateResponseId={templateResponse?.id}
      overrideBuilderId={overrideBuilderId}
      renderOptions={renderOptions}
      projectId={projectId}
      extraInfo={extraInfo}
      resource={resource}
      config={config}
    >
      {children}
    </TemplateRenderer>
  )
}

type ViewProps = BaseProps & {
  templateResponse: TemplateResponse
}

/**
 * Used when viewing an existing template response. Will automatically
 * convert all capture fields to display fields
 */
export const TemplateRendererView: React.FC<ViewProps> = ({
  templateResponse,
  renderOptions,
  overrideBuilderId,
  projectId,
  resource,
  extraInfo,
  config,
  children,
}) => {
  const hasResponseData = useMemo(() => {
    if (!templateResponse?.responseData) return false
    return Object.keys(templateResponse?.responseData).length > 0
  }, [templateResponse])

  if (!hasResponseData) return null

  return (
    <TemplateRenderer
      mode="display"
      form={null}
      isCapture={false}
      template={templateResponse?.responseData}
      originalTemplate={templateResponse?.template?.templateData}
      templateResponseId={templateResponse?.id}
      overrideBuilderId={overrideBuilderId}
      renderOptions={renderOptions}
      projectId={projectId}
      extraInfo={extraInfo}
      resource={resource}
      config={config}
    >
      {children}
    </TemplateRenderer>
  )
}

type Props = BaseProps & {
  mode: TemplateRendererMode
  form: FormInstance
  isCapture: boolean
  template: TemplateData
  originalTemplate?: TemplateData
  templateResponseId?: string | null
}

/**
 * Underlying template rendered. Ideally should only be used in very
 * specific situations when none of the above make sense. E.g. Sign Ons
 */
export const TemplateRenderer: React.FC<Props> = ({
  mode,
  template,
  originalTemplate,
  templateResponseId,
  renderOptions,
  isCapture,
  form,
  extraInfo,
  config,
  overrideBuilderId,
  projectId,
  resource,
  children,
}) => {
  const user = useUser()

  if (!template) return null

  const builderId = overrideBuilderId || user?.builder?.id

  return (
    <TemplateRendererProvider
      mode={mode}
      form={form}
      isCapture={isCapture}
      templateResponseData={template}
      originalTemplateData={originalTemplate}
      templateResponseId={templateResponseId}
      renderOptions={renderOptions}
      extraInfo={{ ...extraInfo }}
      builderId={builderId}
      projectId={projectId}
      resource={resource}
      config={config}
    >
      <InternalTemplateRenderer />

      {children}
    </TemplateRendererProvider>
  )
}

const InternalTemplateRenderer: React.FC = () => {
  const { mode, templateResponseData, form } = useTemplateRenderer()

  const templateData = useMemo(() => {
    if (mode === 'display') {
      return mapCaptureToDisplay(templateResponseData)
    }

    return templateResponseData
  }, [mode, templateResponseData])

  const { description, sections } = templateData

  useEffect(() => {
    form?.setFieldValue(TEMPLATE_RESPONSE_DATA_FIELD, templateResponseData) // store up-to-date local state in form
  }, [templateResponseData, form])

  return (
    <>
      <Form.Item name={TEMPLATE_RESPONSE_DATA_FIELD} noStyle>
        <input type="hidden" />
      </Form.Item>

      {description && <DisplayDescription>{description}</DisplayDescription>}

      {description ? <Divider /> : undefined}

      {sections?.map((section, i) => (
        <Section key={i + 1} section={section} index={i} />
      ))}

      <TemplateAppendix />
    </>
  )
}
